import { logout } from '@features/auth/authSlice';
import {
    BaseQueryApi,
    createApi,
    FetchArgs,
    fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';

// Base query with credentials: 'include' to send cookies automatically
const baseQuery = fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_URL ?? '/api',
    credentials: 'include',  // This ensures cookies are sent with every request
    // No need to set Authorization header - tokens are in HttpOnly cookies
});

// Track last refresh time to avoid too frequent refreshes
let lastRefreshTime = Date.now(); // Start from now to avoid immediate refresh
const REFRESH_INTERVAL = 30 * 60 * 1000; // Refresh tokens every 30 minutes of activity

const baseQueryWithReauth = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object
) => {
    let result = await baseQuery(args, api, extraOptions);

    // Get the URL to check if it's an auth endpoint
    const url = typeof args === 'string' ? args : args.url;
    const isAuthEndpoint = url?.includes('/auth/');

    if (result.error?.status === 401 && !isAuthEndpoint) {
        // Try to refresh token (refresh token is in HttpOnly cookie)
        const refreshResult = await baseQuery(
            {
                url: '/auth/refresh',
                method: 'POST',
            },
            api,
            extraOptions
        );

        if (refreshResult.data) {
            // Token refresh successful, retry the original request
            // New tokens are automatically set in cookies by the server
            lastRefreshTime = Date.now();
            result = await baseQuery(args, api, extraOptions);
        } else {
            // Refresh failed, logout
            api.dispatch(logout());
        }
    } else if (!result.error && !isAuthEndpoint) {
        // On successful requests, proactively refresh tokens if interval passed
        const now = Date.now();
        if (now - lastRefreshTime > REFRESH_INTERVAL) {
            // Refresh tokens in background (don't await, fire and forget)
            Promise.resolve(
                baseQuery(
                    { url: '/auth/refresh', method: 'POST' },
                    api,
                    extraOptions
                )
            ).then((refreshResult) => {
                if (refreshResult.data) {
                    lastRefreshTime = Date.now();
                }
            }).catch(() => {
                // Ignore errors in background refresh
            });
        }
    }

    // Error handling
    if (result.error) {
        const status = result.error.status;
        const data = result.error.data as { message?: string };

        switch (status) {
            case 400:
                toast.error(data?.message || 'Невалидна заявка');
                break;
            case 403:
                toast.error('Нямате права за тази операция');
                break;
            case 404:
                toast.error('Ресурсът не е намерен');
                break;
            case 500:
                toast.error('Сървърна грешка');
                break;
        }
    }

    return result;
};

export const baseApi = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithReauth,
    // Keep cached data for 5 minutes (300 seconds)
    keepUnusedDataFor: 300,
    // Refetch on mount if data is older than 30 seconds
    refetchOnMountOrArgChange: 30,
    // Don't refetch when window regains focus - can cause unnecessary requests
    refetchOnFocus: false,
    tagTypes: [
        'Products',
        'Pallets',
        'Locations',
        'Orders',
        'Movements',
        'StockCounts',
        'ExpiryAlerts',
        'Users',
        'Roles',
        'Suppliers',
        'Customers',
        'Warehouses',
        'Dashboard',
        'Categories',
    ],
    endpoints: () => ({}),
});
